// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@hardwoods/App/Components/Notify";
import XngQuantity from "@hardwoods/App/Components/Quantity/XngQuantity";
import ProductPrice, { ProductPriceStyles } from "@hardwoods/Overrides/Components/ProductPrice";
import SmallHeadingAndText from "@hardwoods/Overrides/Components/SmallHeadingAndText";
import { cartLineBreakpoints } from "@hardwoods/Overrides/Widgets/Cart/helper";
import { xOrderLineGenerateStyles } from "@hardwoods/Overrides/Widgets/Cart/OrderLine.style";
import RfqQuoteDetailsQuotedPricing, {
    RfqQuoteDetailsQuotedPricingStyles,
} from "@hardwoods/Overrides/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuotedPricing";
import Edit from "@hardwoods/XDL/Icons/Edit";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import { hideElement } from "@hardwoods/XDL/Utilities/SharedStyles";
import { maxLengthAttr, qtyRegex } from "@hardwoods/XDL/Utilities/validation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateQuoteLine from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdateQuoteLine";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Checkbox from "@insite/mobius/Checkbox";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    quoteLine: QuoteLineModel;
    checkedItems: any;
    onSelectItem: (id: string) => void;
    extendedStyles?: RfqQuoteDetailsProposedDetailsGridItemStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateQuoteLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsProposedDetailsGridItemStyles {
    quotedPricingText?: TypographyPresentationProps;
    quotedPricingStyles?: RfqQuoteDetailsQuotedPricingStyles;
    quantityTextField?: TextFieldPresentationProps;
    notesAndCostCodeGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    toggleNotesLink?: LinkPresentationProps;
    notesTextArea?: TextAreaProps;
    costCodeGridItem?: GridItemProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    container?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    hidePartNumbersStyles?: ProductPartNumbersStyles;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productImage?: ProductImageStyles;
    brandStyles?: ProductBrandStyles;
    infoAndImageContainer?: InjectableCss;
    lineInfoContainer?: InjectableCss;
    productPartNumbersGridItem?: InjectableCss;
    qtyAndPriceContainer?: InjectableCss;
    priceContainer?: InjectableCss;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    selectedWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    // XNG-Changes
    restrictionNotifyWrapper?: InjectableCss;
    infoMessage?: TypographyPresentationProps;
}

const xRfqQuoteDetailsProposedDetailsGridItemStyles = xOrderLineGenerateStyles(4);

export const rfqQuoteDetailsProposedDetailsGridItemStyles: RfqQuoteDetailsProposedDetailsGridItemStyles = {
    notesAndCostCodeGridItem: {
        css: css`
            display: flex;
            flex: 1;
            align-items: flex-end;
        `,
    },
    toggleNotesLink: {
        icon: {
            iconProps: {
                src: Edit,
                size: 14,
            },
        },
    },
    quotedPricingStyles: {
        quantityText: {
            weight: "bold",
        },
    },
    quotedPricingText: {
        ...mergeWithCss(xRfqQuoteDetailsProposedDetailsGridItemStyles.productInfoQtyOrderedHeadingAndText?.heading, {
            css: css`
                display: none;
                ${xScreen.lte(cartLineBreakpoints.md)} {
                    display: block;
                }
                @media print {
                    display: block;
                }
            `,
        }),
    },
    quantityTextField: {
        labelProps: {
            css: css`
                display: none;
                padding-left: 0;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 0;
                max-width: 80px;
            `,
        },
    },
    selectedWrapper: {
        css: css`
            position: absolute;
            right: 15px;
        `,
    },
    imageWrapper: {
        css: css`
            width: 212px;
        `,
    },
    container: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.cardContainer,
    },
    infoAndImageContainer: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.infoAndImageContainer,
    },
    productImage: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.productImage,
    },
    productDescription: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.productDescriptionStyles,
    },
    productPrice: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.productPrice,
    },
    lineInfoContainer: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.orderLineInfoContainer,
    },
    productPartNumbersGridItem: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.productPartNumbersGridItem,
    },
    productBrandAndDescriptionGridItem: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.productBrandAndDescriptionGridItem,
    },
    productPartNumbers: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.productPartNumbers,
    },
    brandStyles: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.productBrandStyles,
    },
    qtyAndPriceContainer: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.qtyAndPriceContainer,
    },
    priceContainer: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.priceContainer,
    },
    vatLabelText: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.vatLabelText,
    },
    extendedUnitNetPriceText: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.extendedUnitNetPriceText,
    },
    productInfoSubtotalHeadingAndText: {
        ...xRfqQuoteDetailsProposedDetailsGridItemStyles.productInfoSubtotalHeadingAndText,
    },
};

rfqQuoteDetailsProposedDetailsGridItemStyles.hidePartNumbersStyles = {
    ...rfqQuoteDetailsProposedDetailsGridItemStyles.productPartNumbers,
    erpNumberGridItem: {
        width: 0,
        css: css`
            ${hideElement}
        `,
    },
};

const styles = rfqQuoteDetailsProposedDetailsGridItemStyles;

const RfqQuoteDetailsProposedDetailsGridItem = ({
    quote,
    quoteLine,
    enableVat,
    vatPriceDisplay,
    checkedItems,
    showPricing,
    onSelectItem,
    updateQuoteLine,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [showNotes, setShowNotes] = useState(false);
    const [notes, setNotes] = useState(quoteLine.notes);

    const qtyOrderedSubmitHandler = (value: string) => {
        let inputQty = Number.parseInt(value, 10);
        if (Number.isNaN(inputQty)) {
            return;
        }
        // <xng-change: check the value and eliminate invalid chars>
        let transformedValue = value;
        if (value) {
            transformedValue = value.replace(qtyRegex, "") || "1";
            inputQty = Number.parseInt(transformedValue, 10);
        }

        updateQuoteLine({
            quoteId: quote.id,
            quoteLineId: quoteLine.id,
            quoteLine: {
                ...quoteLine,
                qtyOrdered: inputQty,
            },
            onSuccess: (props: any) => {
                // XNG-Changes: show errorMessage if exist
                const errorMessage = props?.errorMessage;
                toasterContext.addToast({
                    body: errorMessage || translate("Quantity updated"),
                    messageType: errorMessage ? "danger" : "success",
                });
            },
            onComplete(resultProps) {
                if (resultProps.result?.quoteLine) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        if (event.currentTarget.value.length > maxLengthAttr.quoteNotes) {
            return;
        }

        setNotes(event.currentTarget.value);
    };

    const notesBlurHandler = () => {
        updateQuoteLine({
            quoteId: quote.id,
            quoteLineId: quoteLine.id,
            quoteLine: {
                ...quoteLine,
                notes,
            },
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Notes updated"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.result?.quoteLine) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const toggleNotesHeading = showNotes
        ? translate("Hide Line Notes")
        : quoteLine.notes.length === 0
        ? translate("Add Line Notes")
        : translate("Show Line Notes");

    const hasSamePrices =
        quoteLine?.properties?.priceUOMNetPrice === quoteLine?.pricing?.unitNetPriceDisplay &&
        quoteLine?.properties?.priceUOM === quoteLine?.unitOfMeasure;

    // XNG-changes
    const description = quoteLine.shortDescription || ("description" in quoteLine ? quoteLine.description : "");

    return (
        <StyledWrapper {...styles.container} style={{ position: "relative" }}>
            <StyledWrapper
                {...styles.infoAndImageContainer}
                data-test-selector={`quoteLine_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
            >
                <StyledWrapper {...styles.imageWrapper}>
                    <ProductImage product={quoteLine} extendedStyles={styles.productImage} />
                </StyledWrapper>
                <StyledWrapper {...styles.lineInfoContainer}>
                    <StyledWrapper {...styles.productPartNumbersGridItem}>
                        <ProductPartNumbers
                            productNumber={quoteLine.erpNumber}
                            extendedStyles={styles.productPartNumbers}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productBrandAndDescriptionGridItem}>
                        {quoteLine.isRestricted || !quoteLine.canAddToCart ? (
                            <Typography>{description}</Typography>
                        ) : (
                            <ProductDescription product={quoteLine} extendedStyles={styles.productDescription} />
                        )}
                        <ProductPartNumbers
                            productNumber={quoteLine.erpNumber}
                            customerProductNumber={quoteLine.customerName}
                            manufacturerItem={quoteLine.manufacturerItem}
                            extendedStyles={styles.hidePartNumbersStyles}
                        />
                        {quoteLine.brand && (
                            <ProductBrand brand={quoteLine.brand} extendedStyles={styles.brandStyles} />
                        )}
                    </StyledWrapper>
                    {quote.showLineNotes && !quoteLine.isPromotionItem && (
                        <StyledWrapper {...styles.notesAndCostCodeGridItem}>
                            <StyledWrapper {...styles.notesGridItem}>
                                <Link {...styles.toggleNotesLink} onClick={toggleNotesClickHandler}>
                                    {toggleNotesHeading}
                                </Link>
                                {showNotes && (
                                    <TextArea
                                        {...styles.notesTextArea}
                                        value={notes}
                                        disabled={!quote.canModifyOrder}
                                        onChange={notesChangeHandler}
                                        onBlur={notesBlurHandler}
                                    />
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.costCodeGridItem}>
                                {quote.showCostCode && (
                                    <>
                                        <Typography {...styles.costCodeLabelText}>{quote.costCodeLabel}</Typography>
                                        {quote.canEditCostCode ? (
                                            <Select {...styles.costCodeSelect}>
                                                {quote.costCodes?.map(costCode => (
                                                    <option key={costCode.costCode} value={costCode.costCode}>
                                                        {costCode.description}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Typography {...styles.costCodeText}>{quoteLine.costCode}</Typography>
                                        )}
                                    </>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {quote.showCostCode && quoteLine.costCode && (
                        <StyledWrapper>
                            <Typography {...styles.costCodeValueText} as="p">
                                <Typography {...styles.costCodeLabelText}>quote.costCodeLabel:</Typography>
                                quoteLine.costCode
                            </Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper
                {...styles.qtyAndPriceContainer}
                data-test-selector="rfqQuoteDetailsProposedDetails_expanded_QtyAndPrice"
            >
                <StyledWrapper {...styles.priceContainer} className={hasSamePrices ? "multiplePrice" : ""}>
                    {!hasSamePrices && (
                        <ProductPrice
                            product={quoteLine}
                            currencySymbol={quote.currencySymbol}
                            showPriceUOM={true}
                            extendedStyles={styles.productPrice}
                        />
                    )}
                    <ProductPrice
                        product={quoteLine}
                        currencySymbol={quote.currencySymbol}
                        extendedStyles={styles.productPrice}
                    />
                </StyledWrapper>
                <StyledWrapper>
                    {showPricing && (
                        <>
                            <Typography {...styles.quotedPricingText}>{translate("Quoted Pricing")}</Typography>
                            <RfqQuoteDetailsQuotedPricing
                                quote={quote}
                                quoteLine={quoteLine}
                                extendedStyles={styles.quotedPricingStyles}
                                displayUOM={true}
                            />
                        </>
                    )}
                </StyledWrapper>
                <StyledWrapper>
                    <XngQuantity
                        qtyValue={quoteLine.qtyOrdered!.toString()}
                        editable={true}
                        disabled={quoteLine.isPromotionItem || quote.isJobQuote}
                        onQtyOrderedChange={qtyOrderedSubmitHandler}
                    />
                </StyledWrapper>
                <StyledWrapper>
                    {quoteLine.pricing && showPricing ? (
                        <>
                            <SmallHeadingAndText
                                heading={
                                    !enableVat
                                        ? translate("Subtotal")
                                        : vatPriceDisplay !== "DisplayWithoutVat"
                                        ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                        : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                }
                                text={
                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? quoteLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : quoteLine.pricing.extendedUnitNetPriceDisplay
                                }
                                extendedStyles={styles.productInfoSubtotalHeadingAndText}
                            />
                            {enableVat && (
                                <>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {vatPriceDisplay === "DisplayWithVat" ||
                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                            ? `${translate("Inc. VAT")} (${quoteLine.pricing.vatRate}%)`
                                            : translate("Ex. VAT")}
                                    </Typography>
                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <>
                                            <Typography {...styles.subtotalWithoutVatText}>
                                                {quoteLine.pricing.extendedUnitNetPriceDisplay}
                                            </Typography>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {translate("Ex. VAT")}
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <>--</>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {/* XNG-Changes */}
            {(quoteLine.isRestricted || !quoteLine.canAddToCart) && (
                <StyledWrapper {...styles.restrictionNotifyWrapper}>
                    <Typography {...styles.infoMessage}>
                        {siteMessage("Hardwoods_QuoteDetails_CannotAddToCart")}
                    </Typography>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.selectedWrapper}>
                <Checkbox
                    checked={checkedItems[quoteLine.id]}
                    onChange={() => {
                        onSelectItem(quoteLine.id);
                    }}
                    // XNG-Changes
                    disabled={quoteLine.isRestricted || !quoteLine.canAddToCart}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsProposedDetailsGridItem);
